import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MessageSquareQuote } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { errorKey } from "@/lib/i18n";
import { AnkaraStrip } from "./ui";
import { PhoneInput } from "./PhoneInput";

const Illustration = () => (
  <div className="relative w-28 h-28 mx-auto" aria-hidden="true">
    <div className="absolute inset-0 rounded-full bg-[#1E4D3B]" />
    <div className="absolute inset-0 rounded-full ankara-dots opacity-70" />
    <div className="absolute inset-6 rounded-full bg-[#F59E0B] flex items-center justify-center text-[#291E18] text-4xl font-extrabold">D</div>
  </div>
);

export const CreateButtons = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button data-testid="empty-create-listing" onClick={() => navigate("/create?type=listing")} className="press min-h-[48px] px-6 rounded-full bg-[#F59E0B] text-[#291E18] font-bold inline-flex items-center justify-center gap-2"><Plus className="w-5 h-5" />{t("create_listing")}</button>
      <button data-testid="empty-make-request" onClick={() => navigate("/create?type=request")} className="press min-h-[48px] px-6 rounded-full border-2 border-[#C2410C] text-[#9A3412] font-bold inline-flex items-center justify-center gap-2"><MessageSquareQuote className="w-5 h-5" />{t("create_request")}</button>
    </div>
  );
};

export const EmptyState = ({ title, children }) => (
  <div data-testid="empty-state" className="rounded-[24px] bg-[#FFFDF9] border border-[#E8E2D9] overflow-hidden text-center">
    <AnkaraStrip />
    <div className="p-8 space-y-5">
      <Illustration />
      <h3 className="text-xl font-extrabold max-w-md mx-auto leading-snug">{title}</h3>
      {children}
    </div>
  </div>
);

export const Waitlist = ({ hub }) => {
  const { t, L, lang } = useApp();
  const [form, setForm] = useState({ name: "", phone: "", preferred_language: lang });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/waitlist", { ...form, hub_id: hub.id }); setDone(true); }
    catch (err) { toast.error(t(errorKey(err) === "invalid_phone" ? "invalid_phone" : "error")); }
    setBusy(false);
  };
  return (
    <EmptyState title={t("soon_title", { city: L(hub) })}>
      {done ? <p data-testid="waitlist-success" className="font-bold text-[#1E4D3B]">{t("waitlist_done", { city: L(hub) })}</p> : (
        <form onSubmit={submit} className="max-w-sm mx-auto space-y-3 text-left" data-testid="waitlist-form">
          <input data-testid="waitlist-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} className="w-full min-h-[48px] rounded-2xl border border-[#E8E2D9] bg-[#FAF7F2] px-4 text-base" />
          <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="waitlist-phone" />
          <div className="flex gap-2">
            {["en", "fr"].map((l) => <button type="button" key={l} data-testid={`waitlist-lang-${l}`} onClick={() => setForm({ ...form, preferred_language: l })} className={`press flex-1 min-h-[48px] rounded-full font-bold ${form.preferred_language === l ? "bg-[#1E4D3B] text-white" : "bg-[#F3EDE4]"}`}>{l === "en" ? "English" : "Français"}</button>)}
          </div>
          <button disabled={busy} data-testid="waitlist-submit" className="press w-full min-h-[52px] rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{t("join_waitlist")}</button>
        </form>
      )}
    </EmptyState>
  );
};
